import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { mapApiError } from "../../../../shared/utils/mapApiError";
import {
  getEvaluacionAssigneeResult,
  getEvaluacionResults,
  gradeEvaluacionAssignee,
} from "../api/evaluacionesApi";

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.1,
        bgcolor: "secondary.50",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function formatDateTime(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusChip({ status }) {
  const labels = {
    PENDING: "Pendiente",
    IN_PROGRESS: "En progreso",
    SUBMITTED: "Entregada",
    EXPIRED: "Vencida",
  };

  const styles = {
    PENDING: { bgcolor: "neutral.300", color: "primary.900" },
    IN_PROGRESS: { bgcolor: "#fff9c4", color: "primary.900" },
    SUBMITTED: { bgcolor: "#b9f6ca", color: "primary.900" },
    EXPIRED: { bgcolor: "#ffcdd2", color: "primary.900" },
  };

  return (
    <Chip
      label={labels[status] || status}
      size="small"
      sx={{
        fontWeight: 800,
        borderRadius: "10px",
        ...styles[status],
      }}
    />
  );
}

function formatScore(value) {
  if (value === null || value === undefined || value === "") return "-";

  const number = Number(value);

  if (!Number.isFinite(number)) return "-";

  return number.toFixed(2);
}

function isFinishedAssignee(assignee) {
  return assignee?.status === "SUBMITTED" || assignee?.status === "EXPIRED";
}

function formatFinishDate(assignee) {
  if (assignee?.submittedAt) {
    return formatDateTime(assignee.submittedAt);
  }

  if (assignee?.status === "EXPIRED") {
    return `Venció: ${formatDateTime(assignee.expiresAt)}`;
  }

  return "-";
}

function hasEmployeeAnswer(item) {
  if (Array.isArray(item?.answer?.selected)) {
    return item.answer.selected.length > 0;
  }

  return typeof item?.answer?.text === "string" && item.answer.text.trim().length > 0;
}

function GradingChip({ status }) {
  const labels = {
    PENDING_REVIEW: "Pendiente de corrección",
    REVIEWED: "Corregida",
    AUTO_GRADED: "Auto corregida",
  };

  const styles = {
    PENDING_REVIEW: { bgcolor: "#fff9c4", color: "primary.900" },
    REVIEWED: { bgcolor: "#b9f6ca", color: "primary.900" },
    AUTO_GRADED: { bgcolor: "secondary.200", color: "primary.900" },
  };

  if (!status) {
    return (
      <Chip
        label="-"
        size="small"
        sx={{ fontWeight: 800, borderRadius: "10px" }}
      />
    );
  }

  return (
    <Chip
      label={labels[status] || status}
      size="small"
      sx={{
        fontWeight: 800,
        borderRadius: "10px",
        ...(styles[status] || {}),
      }}
    />
  );
}

function AnswerDialog({
  open,
  loading,
  detail,
  manualGrades,
  savingGrades,
  onManualGradeChange,
  onSaveGrades,
  onClose,
}) {
  const hasManualReviewQuestions = detail?.answers?.some(
    (item) =>
      (item.questionType === "SHORT_TEXT" ||
        item.questionType === "PARAGRAPH") &&
      hasEmployeeAnswer(item)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {detail?.employee?.fullName
          ? `Respuestas de ${detail.employee.fullName}`
          : "Respuestas del empleado"}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            Cargando respuestas...
          </Typography>
        ) : !detail ? (
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            Sin respuestas para mostrar.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: "primary.900" }}>
                Estado:
              </Typography>

              <StatusChip status={detail.status} />

              <GradingChip status={detail.gradingStatus} />

              <Chip
                label={`Nota: ${
                  detail.scorePercentage !== null && detail.scorePercentage !== undefined
                    ? `${formatScore(detail.scorePercentage)}%`
                    : "Pendiente"
                }`}
                size="small"
                sx={{
                  fontWeight: 800,
                  borderRadius: "10px",
                  bgcolor: "neutral.100",
                  color: "primary.900",
                }}
              />

              <Chip
                label={`Puntos: ${formatScore(detail.score)} / ${formatScore(detail.maxScore)}`}
                size="small"
                sx={{
                  fontWeight: 800,
                  borderRadius: "10px",
                  bgcolor: "neutral.100",
                  color: "primary.900",
                }}
              />
            </Stack>

            {detail.answers?.map((item, index) => {
              const isTextQuestion =
                item.questionType === "SHORT_TEXT" ||
                item.questionType === "PARAGRAPH";

              const hasAnswer = hasEmployeeAnswer(item);

              const gradeValue =
                manualGrades?.[item.questionId]?.pointsAwarded ?? "";

              const feedbackValue =
                manualGrades?.[item.questionId]?.feedback ?? "";

              return (
                <Box
                  key={item.questionId}
                  sx={{
                    p: 2,
                    borderRadius: "18px",
                    bgcolor: "neutral.50",
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{
                        alignItems: { xs: "flex-start", sm: "center" },
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                        {index + 1}. {item.question}
                      </Typography>

                      <Chip
                        label={`Puntaje: ${formatScore(item.pointsAwarded)} / ${formatScore(item.maxPoints)}`}
                        size="small"
                        sx={{
                          fontWeight: 800,
                          borderRadius: "10px",
                          bgcolor: "white",
                          color: "primary.900",
                        }}
                      />
                    </Stack>

                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "neutral.600" }}>
                      Tipo: {item.questionType}
                    </Typography>

                    {Array.isArray(item.options) && item.options.length > 0 && (
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {item.options.map((option) => {
                          const selected = item.answer?.selected?.includes(option.id);
                          const correct = item.correct?.includes(option.id);

                          return (
                            <Typography
                              key={option.id}
                              sx={{
                                fontSize: 13,
                                fontWeight: 800,
                                color: correct
                                  ? "success.main"
                                  : selected
                                  ? "primary.900"
                                  : "neutral.700",
                              }}
                            >
                              {selected ? "●" : "○"} {option.text}
                              {correct ? "  ✓ correcta" : ""}
                            </Typography>
                          );
                        })}
                      </Stack>
                    )}

                    {item.answer?.text && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1.5,
                          borderRadius: "14px",
                          bgcolor: "white",
                          border: "1px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "neutral.600" }}>
                          Respuesta del empleado
                        </Typography>

                        <Typography sx={{ mt: 0.5, fontSize: 13, fontWeight: 700, color: "primary.900" }}>
                          {item.answer.text}
                        </Typography>
                      </Box>
                    )}

                    {!hasAnswer && (
                      <Chip
                        label="Sin responder"
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          fontWeight: 800,
                          borderRadius: "10px",
                          bgcolor: "neutral.200",
                          color: "primary.900",
                        }}
                      />
                    )}

                    {!isTextQuestion && item.isCorrect !== null && item.isCorrect !== undefined && (
                      <Chip
                        label={item.isCorrect ? "Correcta" : "Incorrecta / Parcial"}
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          fontWeight: 800,
                          borderRadius: "10px",
                          bgcolor: item.isCorrect ? "#b9f6ca" : "#ffcdd2",
                          color: "primary.900",
                        }}
                      />
                    )}

                    {isTextQuestion && hasAnswer && (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ mt: 1 }}
                      >
                        <TextField
                          label={`Puntaje obtenido / ${formatScore(item.maxPoints)}`}
                          type="number"
                          size="small"
                          value={gradeValue}
                          onChange={(event) =>
                            onManualGradeChange(item.questionId, "pointsAwarded", event.target.value)
                          }
                          inputProps={{
                            min: 0,
                            max: Number(item.maxPoints ?? item.points ?? 1),
                            step: 0.25,
                          }}
                          sx={{
                            width: { xs: "100%", sm: 220 },
                            "& .MuiOutlinedInput-root": { borderRadius: "14px" },
                          }}
                        />

                        <TextField
                          label="Feedback"
                          size="small"
                          value={feedbackValue}
                          onChange={(event) =>
                            onManualGradeChange(item.questionId, "feedback", event.target.value)
                          }
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: "14px" },
                          }}
                        />
                      </Stack>
                    )}

                    {item.feedback && (
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "neutral.600" }}>
                        Feedback guardado: {item.feedback}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {hasManualReviewQuestions && (
          <Button
            onClick={onSaveGrades}
            disabled={savingGrades || loading || !detail}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "18px",
              bgcolor: "primary.500",
              color: "logoblanco",
              "&:hover": { bgcolor: "primary.600" },
              "&.Mui-disabled": { bgcolor: "neutral.200", color: "neutral.600" },
            }}
          >
            {savingGrades ? "Guardando..." : "Guardar corrección"}
          </Button>
        )}

        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 800 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EvaluacionResultadosPage() {
  const navigate = useNavigate();
  const { evaluationId } = useParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const [manualGrades, setManualGrades] = useState({});
  const [savingGrades, setSavingGrades] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);

      try {
        const nextData = await getEvaluacionResults(evaluationId);

        if (!alive) return;

        setData(nextData);
        setError("");
      } catch (err) {
        if (!alive) return;
        setError(mapApiError(err).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [evaluationId]);

  const buildManualGradesFromDetail = (detailData) => {
    const next = {};

    for (const item of detailData?.answers ?? []) {
      const isTextQuestion =
        item.questionType === "SHORT_TEXT" ||
        item.questionType === "PARAGRAPH";

      if (isTextQuestion && hasEmployeeAnswer(item)) {
        next[item.questionId] = {
          pointsAwarded:
            item.pointsAwarded !== null && item.pointsAwarded !== undefined
              ? String(item.pointsAwarded)
              : "",
          feedback: item.feedback ?? "",
        };
      }
    }

    return next;
  };

  const refreshResults = async () => {
    const nextData = await getEvaluacionResults(evaluationId);
    setData(nextData);
  };

  const handleManualGradeChange = (questionId, field, value) => {
    setManualGrades((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [field]: value,
      },
    }));
  };

  const openDetail = async (assigneeId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    setManualGrades({});

    try {
      const nextDetail = await getEvaluacionAssigneeResult(evaluationId, assigneeId);
      setDetail(nextDetail);
      setManualGrades(buildManualGradesFromDetail(nextDetail));
    } catch (err) {
      window.alert(mapApiError(err).message);
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveGrades = async () => {
    if (!detail?.assigneeId) return;

    const answers = Object.entries(manualGrades)
      .map(([questionId, value]) => ({
        questionId: Number(questionId),
        pointsAwarded: Number(value.pointsAwarded),
        feedback: value.feedback?.trim() || null,
      }))
      .filter((item) => Number.isFinite(item.pointsAwarded));

    if (answers.length === 0) {
      window.alert("No hay notas manuales para guardar.");
      return;
    }

    for (const item of answers) {
      const question = detail.answers?.find(
        (answer) => Number(answer.questionId) === Number(item.questionId)
      );

      const maxPoints = Number(question?.maxPoints ?? question?.points ?? 0);

      if (item.pointsAwarded < 0 || item.pointsAwarded > maxPoints) {
        window.alert(
          `La nota de la pregunta ${question?.orderIndex || item.questionId} debe estar entre 0 y ${maxPoints}.`
        );
        return;
      }
    }

    setSavingGrades(true);

    try {
      const updatedDetail = await gradeEvaluacionAssignee(
        evaluationId,
        detail.assigneeId,
        { answers }
      );

      setDetail(updatedDetail);
      setManualGrades(buildManualGradesFromDetail(updatedDetail));
      await refreshResults();
    } catch (err) {
      window.alert(mapApiError(err).message);
    } finally {
      setSavingGrades(false);
    }
  };

  const summary = data?.summary;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <IconButton
          onClick={() => navigate("/rrhh/contenido")}
          sx={{ width: 36, height: 36, bgcolor: "secondary.500", "&:hover": { bgcolor: "secondary.600" } }}
        >
          <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
        </IconButton>

        <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
          <Pill sx={{ bgcolor: "secondary.200" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>
              Resultados de evaluación
            </Typography>
          </Pill>
        </Stack>
      </Stack>

      {!!error && (
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "error.main" }}>
          {error}
        </Typography>
      )}

      <Box sx={{ bgcolor: "white", borderRadius: "24px", p: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        {loading ? (
          <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
            Cargando resultados...
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: "primary.900" }}>
              {data?.evaluation?.title || "Evaluación"}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Chip label={`Total: ${summary?.total ?? 0}`} sx={{ fontWeight: 800 }} />
              <Chip label={`Pendientes: ${summary?.pending ?? 0}`} sx={{ fontWeight: 800 }} />
              <Chip label={`En progreso: ${summary?.inProgress ?? 0}`} sx={{ fontWeight: 800 }} />
              <Chip label={`Entregadas: ${summary?.submitted ?? 0}`} sx={{ fontWeight: 800 }} />
              <Chip label={`Vencidas: ${summary?.expired ?? 0}`} sx={{ fontWeight: 800 }} />
            </Stack>

            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1fr 1.1fr 0.8fr 1fr 1fr 80px",
                  gap: 1,
                  bgcolor: "neutral.300",
                  borderRadius: "16px",
                  px: 2,
                  py: 1,
                }}
              >
                {["Empleado", "Estado", "Corrección", "Nota", "Inicio", "Entrega", "Ver"].map((head) => (
                  <Typography key={head} sx={{ fontSize: 13, fontWeight: 900, color: "primary.900" }}>
                    {head}
                  </Typography>
                ))}
              </Box>

              <Stack sx={{ mt: 1 }}>
                {data?.assignees?.map((assignee) => (
                  <Box
                    key={assignee.assigneeId}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1.4fr 1fr 1.1fr 0.8fr 1fr 1fr 80px",
                      gap: 1,
                      alignItems: "center",
                      px: 2,
                      py: 1,
                      borderBottom: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>
                      {assignee.employeeName || `Empleado ${assignee.employeeId}`}
                    </Typography>

                    <Box>
                      <StatusChip status={assignee.status} />
                    </Box>

                    <Box>
                      <GradingChip status={assignee.gradingStatus} />
                    </Box>

                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: "primary.900" }}>
                      {assignee.scorePercentage !== null && assignee.scorePercentage !== undefined
                        ? `${formatScore(assignee.scorePercentage)}%`
                        : "-"}
                    </Typography>

                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "neutral.700" }}>
                      {formatDateTime(assignee.startedAt)}
                    </Typography>

                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "neutral.700" }}>
                      {formatFinishDate(assignee)}
                    </Typography>

                    <IconButton
                      size="small"
                      disabled={!isFinishedAssignee(assignee)}
                      onClick={() => openDetail(assignee.assigneeId)}
                      sx={{
                        bgcolor: "neutral.100",
                        opacity: isFinishedAssignee(assignee) ? 1 : 0.45,
                        "&:hover": { bgcolor: "neutral.200" },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 17, color: "primary.900" }} />
                    </IconButton>
                  </Box>
                ))}

                {!data?.assignees?.length && (
                  <Typography sx={{ px: 2, py: 2, fontSize: 13, fontWeight: 700, color: "neutral.700" }}>
                    No hay empleados asignados.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>

      <AnswerDialog
        open={detailOpen}
        loading={detailLoading}
        detail={detail}
        manualGrades={manualGrades}
        savingGrades={savingGrades}
        onManualGradeChange={handleManualGradeChange}
        onSaveGrades={handleSaveGrades}
        onClose={() => {
          setDetailOpen(false);
          setDetail(null);
          setManualGrades({});
        }}
      />
    </Stack>
  );
}